import { useCallback, useEffect, useMemo } from 'react';
import { telemetry } from '@/telemetry-browser';
import { metrics } from '@opentelemetry/api';
import { frontendTraceManager } from '@/telemetry/FrontendTraceManager';

/**
 * Hook to provide easy access to Voxi custom metrics
 * Integrates with the existing monitoring system
 */
export function useTelemetryMetrics() {
  // Initialize custom metrics
  const customMetrics = useMemo(() => telemetry.createCustomMetrics(), []);

  // Video call violation tracking
  const recordViolation = useCallback((
    type: string, 
    severity: 'low' | 'medium' | 'high' | 'critical' = 'medium',
    additionalAttributes?: Record<string, string | number>
  ) => {
    customMetrics.violationCounter.add(1, {
      type,
      severity,
      ...additionalAttributes,
    });

    // Add violation event to parent trace instead of creating separate spans
    try {
      frontendTraceManager.addEvent('metrics.violation', {
        'voxi.violation.type': type,
        'voxi.violation.severity': severity,
        'voxi.violation.timestamp': new Date().toISOString(),
        'voxi.violation.source': 'metrics_hook',
        ...additionalAttributes,
      });
    } catch (error) {
      console.warn('Failed to add violation event from metrics hook:', error);
    }
  }, [customMetrics]);

  // Call duration tracking
  const recordCallDuration = useCallback((
    durationSeconds: number,
    callType: string,
    status: 'completed' | 'failed' | 'dropped' = 'completed',
    additionalAttributes?: Record<string, string | number>
  ) => {
    customMetrics.callDurationHistogram.record(durationSeconds, {
      call_type: callType,
      status,
      ...additionalAttributes,
    });
  }, [customMetrics]);

  // API latency tracking
  const recordApiLatency = useCallback((
    latencyMs: number,
    endpoint: string,
    method: string = 'GET',
    statusCode?: number,
    additionalAttributes?: Record<string, string | number>
  ) => {
    customMetrics.apiLatencyHistogram.record(latencyMs, {
      endpoint,
      method,
      status_code: statusCode?.toString() || 'unknown',
      ...additionalAttributes,
    });
  }, [customMetrics]);

  // Page load time tracking
  const recordPageLoadTime = useCallback((
    loadTimeMs: number,
    pageName: string,
    additionalAttributes?: Record<string, string | number>
  ) => {
    customMetrics.pageLoadHistogram.record(loadTimeMs, {
      page: pageName,
      ...additionalAttributes,
    });
  }, [customMetrics]);

  // User interaction tracking
  const recordUserInteraction = useCallback((
    action: string,
    component: string,
    additionalAttributes?: Record<string, string | number>
  ) => {
    const meter = metrics.getMeter('voxi-frontend');
    const interactionCounter = meter.createCounter('voxi.user.interactions', {
      description: 'Count of user interactions',
    });

    interactionCounter.add(1, {
      action,
      component,
      ...additionalAttributes,
    });
  }, []);

  // Performance tracking
  const recordPerformanceMetric = useCallback((
    metricName: string,
    value: number,
    unit: string = 'ms',
    additionalAttributes?: Record<string, string | number>
  ) => {
    const meter = metrics.getMeter('voxi-frontend');
    const performanceHistogram = meter.createHistogram(`voxi.performance.${metricName}`, {
      description: `Performance metric: ${metricName}`,
      unit,
    });

    performanceHistogram.record(value, additionalAttributes);
  }, []);

  // Automatic page load time measurement
  useEffect(() => {
    const handleLoad = () => {
      if (typeof window !== 'undefined' && window.performance) {
        const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
        if (navigation) {
          const loadTime = navigation.loadEventEnd - navigation.fetchStart;
          recordPageLoadTime(loadTime, window.location.pathname);
        }
      }
    };

    if (document.readyState === 'complete') {
      handleLoad();
    } else {
      window.addEventListener('load', handleLoad);
      return () => window.removeEventListener('load', handleLoad);
    }
  }, [recordPageLoadTime]);

  return {
    // Metrics recording functions
    recordViolation,
    recordCallDuration,
    recordApiLatency,
    recordPageLoadTime,
    recordUserInteraction,
    recordPerformanceMetric,
    
    // Direct access to custom metrics if needed
    customMetrics,
  };
}